import { useState, useEffect, useCallback } from 'react';
import { API_ROOT } from '@/config';

export interface SubscriptionStatus {
  has_subscription: boolean;
  status: string;
  plan_id: string | null;
  plan_name: string;
  monthly_price: number;
  monthly_limit: number;
  current_usage: number;
  remaining: number;
  can_analyze: boolean;
  current_period_end: string | null;
  is_trial?: boolean;
  trial_days_remaining?: number | null;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  monthly_price: number;
  monthly_video_limit: number;
  stripe_price_id: string | null;
}

export const useSubscription = () => {
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsLoading(false);
        return;
      }

      const response = await fetch(`${API_ROOT}/subscription/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      } else {
        setError('Failed to fetch subscription status');
      }
    } catch (err) {
      setError('Error fetching subscription status');
      console.error('Subscription fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchPlans = useCallback(async () => {
    try {
      const response = await fetch(`${API_ROOT}/subscription/plans`);
      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans || []);
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
    }
  }, []);

  const createCheckoutSession = async (plan: string = 'starter') => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      // Pass the current frontend URL so Stripe redirects back to the correct domain
      const frontendUrl = window.location.origin;

      const response = await fetch(`${API_ROOT}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ plan, frontend_url: frontendUrl })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      return data;
    } catch (err) {
      console.error('Checkout session error:', err);
      throw err;
    }
  };

  const createBillingPortalSession = async (): Promise<{ portal_url: string }> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      // Pass the current frontend URL so Stripe redirects back to the correct domain
      const frontendUrl = window.location.origin;

      const response = await fetch(`${API_ROOT}/create-billing-portal-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ frontend_url: frontendUrl })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create billing portal session');
      }

      const data = await response.json();
      return data;
    } catch (err) {
      console.error('Billing portal error:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchSubscriptionStatus();
    fetchPlans();
  }, [fetchSubscriptionStatus, fetchPlans]);

  return {
    subscription,
    plans,
    isLoading,
    error,
    refresh: fetchSubscriptionStatus,
    createCheckoutSession,
    createBillingPortalSession
  };
};

export default useSubscription;

